import React, {useEffect} from "react";
import {observer} from "mobx-react-lite";
import {useLocation} from "react-router-dom";
import {useStores} from "stores/rootStore";
import {AUTH_ONLY_EXIT, useAuth} from "hooks/useAuth";
import {useLocalizedPath} from "i18n/hooks";

export const AuthGuard: React.FC<{children: React.ReactNode}> = observer(({children}) => {
    const {user} = useStores();
    const {login} = useAuth();
    const {pathname, search} = useLocation();
    const localizedPath = useLocalizedPath();

    useEffect(() => {
        // A logout turns the account anon right here while the page is already on its way out:
        // a login now would only save the account's draft for the next page to restore.
        if (!user.isAnonymous || user.loggingOut) return;
        // The login outlives this page (Back leaves it open): closed after that, it must not leave.
        let here = true;
        login({
            action: {type: 'account_page', path: pathname + search},
            // Nothing on this page is for an anon: closing the login leaves it. A page load, not a
            // client navigation: the guest session starts only on boot, and this page started none.
            onDismiss: () => { if (here) window.location.replace(localizedPath(AUTH_ONLY_EXIT)); },
        });
        return () => { here = false; };
    }, [user.isAnonymous, user.loggingOut]);

    if (user.isAnonymous) return null;

    return <>{children}</>;
});
